import type { Component } from 'vue'
import { defineAsyncComponent } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import type { Workspace } from '~/modules/auth/types'

/**
 * The dashboard workspaces: their key, their home and the identity strip their topbar
 * carries. **Nothing else.**
 *
 * Phase 13 split this module in two. The router imports it eagerly to build `workspaceRoutes`,
 * and until the split it also carried every sidebar group and its ~33 `@vicons/fluent` icon
 * pairs -- which put the whole dashboard menu into the entry chunk a marketplace visitor
 * downloads on Nigerian mobile data. The menus now live in `router/workspace-menu.ts`, which
 * only the dashboard layout imports, and that layout is itself a lazy chunk. Two budget raises
 * in two phases were this file's weight; the entry lost more than ten kilobytes.
 *
 * Route-map.md rule 4 still holds: never a nav entry that leads to a blank page. The menu
 * module's test asserts every entry against the registered routes.
 */
export interface WorkspaceDefinition {
  key: Workspace
  home: string
  /** i18n key for the sidebar brand line. */
  labelKey: string
  /**
   * Rendered in the topbar, left of the actions, on every screen of the workspace.
   *
   * Affiliate Operations uses it for the officer's code, which has to be copyable in one tap
   * from any screen *including 360px*, where the sidebar is a closed drawer
   * (affiliate-workspace.md §7). Async so a landlord never downloads it.
   */
  identity?: Component
  /** The module declares its own routes; do not generate a placeholder for this workspace. */
  ownsRoutes?: boolean
}

export const WORKSPACES: WorkspaceDefinition[] = [
  {
    key: 'landlord',
    home: '/app/landlord',
    labelKey: 'workspaces.landlord.title',
    ownsRoutes: true,
  },
  {
    key: 'affiliate',
    home: '/app/affiliate',
    labelKey: 'workspaces.affiliate.title',
    identity: defineAsyncComponent(
      () => import('~/modules/affiliate/components/AffiliateWorkspaceIdentity.vue'),
    ),
    ownsRoutes: true,
  },
  {
    key: 'admin',
    home: '/app/admin',
    labelKey: 'workspaces.admin.title',
    ownsRoutes: true,
  },
  // 2026-10-12 (requesters-support-whatsapp.md §6): APMP's support officers.
  { key: 'support', home: '/app/support', labelKey: 'workspaces.support.title', ownsRoutes: true },
  // 2026-10-22: an affiliate institution's staff (UNIZIK, COOU), read-only.
  {
    key: 'institution',
    home: '/app/institution',
    labelKey: 'workspaces.institution.title',
    ownsRoutes: true,
  },
]

export const workspaceRoutes: RouteRecordRaw[] = WORKSPACES.filter(
  (workspace) => !workspace.ownsRoutes,
).map((workspace) => ({
  path: workspace.home,
  name: `workspace-${workspace.key}`,
  component: () => import('~/modules/platform/pages/WorkspaceHomePage.vue'),
  meta: {
    layout: 'dashboard',
    requiresAuth: true,
    workspace: workspace.key,
    title: workspace.labelKey,
  },
}))
